<template>
  <div
    class="company"
    :class="{
      '!cursor-not-allowed':
        company.company.role === CompanyAccessRole.SUSPENDED,
    }">
    <!-- Avatar -->
    <div>
      <SmAvatar
        :name="company.company.name"
        class="bg-gray-200"
        form="circle" />
    </div>
    <!-- Company name -->
    <div>
      <div class="flex items-center gap-2">
        <span>{{ company.company.name }}</span>
        <SmTooltip v-if="company.company.region" placement="top">
          <Icon :icon="getRegionIconName(company.company.region)" />
          <template #content>
            {{ getRegionTooltipText(company.company.region) }}
          </template>
        </SmTooltip>
      </div>
      <div class="text-contrast-muted">
        {{ capitalize(i18n.t(company.company.role)) }}
      </div>
    </div>
    <div class="mb-6 ml-auto">
      <!-- Last login -->
      <div v-if="company.company.last_login">
        <SmTooltip placement="right">
          <span>
            {{
              i18n.t(
                'daysAgo',
                [getDaysToNow(company.company.last_login)],
                getDaysToNow(company.company.last_login)
              )
            }}
          </span>

          <!-- Tooltip Content -->
          <template #content>
            <div>
              {{ i18n.t('lastLogin') }}:
              {{
                displayDate(new Date(company.company.last_login), {
                  dateStyle: 'short',
                })
              }}
            </div>
          </template>
        </SmTooltip>
      </div>
      <div v-else>
        {{ i18n.t('never') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Icon } from '@iconify/vue'
  import dayjs from 'dayjs'
  import { CompanyAccessRole } from '@/client'
  import { capitalize } from '@/utils/formatter/formatter'
  import { displayDate } from '@/utils/formatter/date'
  import { currencyCodeToIconName } from '@/utils/currency/currencyUtil'

  import { useI18n } from 'vue-i18n'

  import { AvailableCompany } from '@/client'

  const i18n = useI18n()

  const company = defineProps({
    company: {
      type: Object as PropType<AvailableCompany & { region?: 'EU' | 'US' }>,
      required: true,
    },
  })

  function getDaysToNow(date: string) {
    const now = dayjs()
    const then = dayjs(date)
    return now.diff(then, 'day')
  }

  function getRegionIconName(region: 'EU' | 'US') {
    const currencyCode = region === 'US' ? 'USD' : 'EUR'
    return currencyCodeToIconName(currencyCode)
  }

  function getRegionTooltipText(region: 'EU' | 'US') {
    return region === 'US'
      ? i18n.t('companyHostedInUs')
      : i18n.t('companyHostedInEu')
  }
</script>
